import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatNumber, formatLatency, formatCost } from '@/lib/utils';
import { 
  Activity, 
  Zap, 
  Database, 
  DollarSign,
  TrendingUp,
  Shield,
  Clock
} from 'lucide-react';

const iconMap = {
  requests: Activity,
  latency: Zap,
  cache: Database,
  cost: DollarSign,
  savings: TrendingUp,
  pii: Shield,
  uptime: Clock,
};

export function MetricCard({ 
  title, 
  value, 
  subtitle, 
  icon = 'requests', 
  trend,
  color = 'primary',
  format = 'number'
}) {
  const Icon = iconMap[icon] || Activity;

  const formatValue = (val) => {
    switch (format) {
      case 'latency':
        return formatLatency(val);
      case 'cost':
        return formatCost(val);
      case 'percent':
        return `${val}%`;
      case 'time':
        const hours = Math.floor(val / 3600);
        const mins = Math.floor((val % 3600) / 60);
        return `${hours}h ${mins}m`;
      default:
        return formatNumber(val);
    }
  };

  const colorClasses = {
    primary: 'text-primary bg-primary/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    violet: 'text-violet-400 bg-violet-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
  };

  return (
    <Card className="metric-card border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className={cn('p-2 rounded-md', colorClasses[color])}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-heading font-bold text-foreground">
            {formatValue(value)}
          </span>
          {trend && (
            <span className={cn(
              'text-xs font-mono',
              trend > 0 ? 'text-emerald-400' : 'text-red-400'
            )}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          )}
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default MetricCard;
